// L'éditeur des documents du club (D-171, maquettes validées D-172) : le texte en blocs, les champs remplis tout seuls, les
// trois modèles pour démarrer, ce qui change d'une version à l'autre, le tableur de l'export.
// Le texte d'un bloc garde deux marques : **gras** et {champ} (club, saison, lic, par, date) ; ==…== marque ce qui change
// (calculé, jamais enregistré). La base vérifie la même forme (_dc_body_ok) et calcule l'empreinte de chaque version.
import { acceptText } from './accords';

export type FieldKey = 'club' | 'saison' | 'lic' | 'par' | 'date';
export const FIELD_KEYS: FieldKey[] = ['club', 'saison', 'lic', 'par', 'date'];
/** L'étiquette d'un champ, telle que l'éditeur la montre. */
export const FIELD_LABEL: Record<FieldKey, string> = {
  club: 'nom du club', saison: 'saison', lic: 'nom du licencié', par: 'nom du parent', date: 'date du jour',
};
export type FieldValues = Partial<Record<FieldKey, string>>;

export interface Box { t: string; req: boolean }
export type Who = 'principal' | 'licencie';
export type Block =
  | { t: 'h1' | 'sub' | 'h2' | 'p'; s: string }
  | { t: 'ul'; li: string[] }
  | { t: 'enc'; who: Who; boxes: Box[] };
export type Body = Block[];
export type ModelKey = 'reglement' | 'autorisation' | 'charte' | 'blanche';

// ---------------------------------------------------------------------------------------------------------------------
// Le texte d'un bloc
// ---------------------------------------------------------------------------------------------------------------------
const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const isField = (k: string): k is FieldKey => (FIELD_KEYS as string[]).includes(k);

/** Le texte d'un bloc en HTML : `edit` (les champs en étiquettes), `fill` (remplis et soulignés, l'aperçu), `plain` (remplis). */
export function inlineHtml(s: string, mode: 'edit' | 'fill' | 'plain', values: FieldValues = {}): string {
  return esc(s)
    .replace(/==(.+?)==/g, '<mark class="dc-new">$1</mark>')
    .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
    .replace(/\{(\w+)\}/g, (m, k: string) => {
      if (!isField(k)) return m;
      if (mode === 'edit') return `<span class="dc-f" contenteditable="false" data-f="${k}">${FIELD_LABEL[k]}</span>`;
      const v = esc(values[k] ?? FIELD_LABEL[k]);
      return mode === 'fill' ? `<span class="dc-fill">${v}</span>` : v;
    });
}

/** Le texte nu (sans marques), les champs remplis (ou entre crochets s'ils ne le sont pas). */
export function plainText(s: string, values: FieldValues = {}): string {
  return s.replace(/==(.+?)==/g, '$1').replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\{(\w+)\}/g, (m, k: string) => (isField(k) ? values[k] ?? `[${FIELD_LABEL[k]}]` : m));
}

/** Les morceaux d'un texte (pour le PDF) : gras ou non, champs remplis. */
export function runs(s: string, values: FieldValues = {}): { t: string; b: boolean }[] {
  const out: { t: string; b: boolean }[] = [];
  const src = s.replace(/==(.+?)==/g, '$1');
  let i = 0;
  for (const m of src.matchAll(/\*\*(.+?)\*\*/g)) {
    if (m.index! > i) out.push({ t: plainText(src.slice(i, m.index), values), b: false });
    out.push({ t: plainText(m[1]!, values), b: true });
    i = m.index! + m[0].length;
  }
  if (i < src.length) out.push({ t: plainText(src.slice(i), values), b: false });
  return out.filter(r => r.t);
}

// ---------------------------------------------------------------------------------------------------------------------
// Les encarts
// ---------------------------------------------------------------------------------------------------------------------
export const encartOf = (body: Body, who: Who) => body.find((b): b is Extract<Block, { t: 'enc' }> => b.t === 'enc' && b.who === who) ?? null;
export const hasPrincipal = (body: Body) => !!encartOf(body, 'principal');

/** Ce qui manque pour publier (null : c'est bon). Même règle que la base. */
export function publishBlocker(body: Body): string | null {
  const encs = body.filter(b => b.t === 'enc') as Extract<Block, { t: 'enc' }>[];
  if (!encs.some(e => e.who === 'principal')) return 'Il manque l’encart Signature : place-le là où le parent signe.';
  if (encs.filter(e => e.who === 'principal').length > 1 || encs.filter(e => e.who === 'licencie').length > 1) return 'Un seul encart par signataire.';
  if (encs.some(e => e.boxes.some(x => x.t.trim().length < 3))) return 'Une case de l’encart est vide.';
  return null;
}

/** La phrase de la case : « J’ai lu et j’accepte le règlement intérieur du … (version du 3 novembre 2026). » */
export function acceptLine(title: string, club: string, vdate?: string | null): string {
  const t = acceptText(title, club);
  return vdate ? `${t.slice(0, -1)} (version du ${vdate}).` : t;
}

/** « 3 novembre 2026 », « 1er septembre 2026 » (dans le fuseau du club). */
export function dayFr(iso: string | Date, tz?: string): string {
  const d = typeof iso === 'string' ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso) : iso;
  const s = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', ...(tz ? { timeZone: tz } : {}) });
  return s.replace(/^1 /, '1er ');
}

// ---------------------------------------------------------------------------------------------------------------------
// Les modèles (écrits par Arceau pour démarrer, fictifs, à faire relire par un juriste avant de publier). Sans encart :
// c'est le club qui le place.
// ---------------------------------------------------------------------------------------------------------------------
export const MODELS: { key: ModelKey; title: string; sub: string; minors: boolean; adults: boolean }[] = [
  { key: 'reglement', title: 'Règlement intérieur', sub: '7 articles à adapter', minors: true, adults: true },
  { key: 'autorisation', title: 'Autorisation parentale', sub: 'Soins d’urgence, sortie de la salle', minors: true, adults: false },
  { key: 'charte', title: 'Charte du joueur', sub: '3 engagements du joueur', minors: true, adults: true },
  { key: 'blanche', title: 'Page blanche', sub: 'Un titre, à toi d’écrire', minors: true, adults: true },
];

export function modelBody(k: ModelKey): Body {
  if (k === 'reglement') return [
    { t: 'h1', s: 'Règlement intérieur' },
    { t: 'sub', s: '{club} · saison {saison}' },
    { t: 'p', s: 'Ce règlement complète les statuts de l’association. Il s’applique pour la saison {saison} à {lic}, comme à tous les licenciés du {club}, aux parents des licenciés mineurs, aux bénévoles et aux dirigeants. Il a été adopté par le bureau.' },
    { t: 'h2', s: 'Article 1 · Adhésion' },
    { t: 'p', s: 'Est membre du club toute personne licenciée à la FFBB au {club} et à jour de sa cotisation. La licence n’est demandée à la fédération qu’une fois le dossier complet : documents signés, cotisation réglée, questionnaire de santé rempli sur e-Licence.' },
    { t: 'h2', s: 'Article 2 · Entraînements' },
    { t: 'ul', li: ['On arrive en tenue, **cinq minutes avant** le début de la séance.', 'Une absence se signale dans l’app dès qu’on la connaît.', 'Un licencié mineur reste sous la responsabilité de ses parents jusqu’à l’arrivée de son entraîneur dans la salle, et de nouveau dès la fin de la séance.'] },
    { t: 'h2', s: 'Article 3 · Matchs et déplacements' },
    { t: 'p', s: 'Le licencié convoqué répond à la convocation dans l’app. Les déplacements se font en covoiturage, dans le respect des accords donnés par les parents dans l’app.' },
    { t: 'h2', s: 'Article 4 · Respect' },
    { t: 'p', s: 'Chacun respecte ses partenaires, les adversaires, les arbitres, les officiels de table et le public. Les propos ou les gestes violents, racistes, sexistes ou discriminatoires ne sont pas tolérés, sur le terrain, en tribune comme sur les réseaux.' },
    { t: 'h2', s: 'Article 5 · Salles et matériel' },
    { t: 'p', s: 'Les salles sont prêtées par la commune : on les laisse propres. Le matériel du club est confié à chacun ; un dégât volontaire reste à la charge de son auteur.' },
    { t: 'h2', s: 'Article 6 · Cotisation' },
    { t: 'p', s: 'La cotisation se règle en une ou plusieurs fois, selon ce que le bureau propose. Elle n’est pas remboursée après le début des championnats, sauf raison médicale.' },
    { t: 'h2', s: 'Article 7 · Sanctions' },
    { t: 'p', s: 'Un manquement grave au présent règlement peut entraîner, après avoir entendu l’intéressé (et ses parents s’il est mineur), un avertissement ou une suspension décidés par le bureau.' },
  ];
  if (k === 'autorisation') return [
    { t: 'h1', s: 'Autorisation parentale' },
    { t: 'sub', s: '{club} · saison {saison}' },
    { t: 'p', s: '{par}, parent de {lic}, licencié du {club} pour la saison {saison}, donne au club les autorisations ci-dessous.' },
    { t: 'h2', s: 'Soins d’urgence' },
    { t: 'p', s: 'En cas d’urgence, les responsables du club peuvent prendre toute mesure nécessaire pour la santé de l’enfant : appel des secours, transport à l’hôpital. Le parent s’engage à rester joignable et à tenir à jour son numéro dans l’app.' },
    { t: 'h2', s: 'Sortie de la salle' },
    { t: 'p', s: 'À la fin de l’entraînement, l’enfant attend dans la salle qu’un parent vienne le chercher, sauf si la case prévue est cochée dans l’encart de signature.' },
    { t: 'h2', s: 'Photos et transport' },
    { t: 'p', s: 'Les accords pour les photos et le transport se donnent dans l’app, dans la fiche de l’enfant : ce document ne les redemande pas.' },
  ];
  if (k === 'charte') return [
    { t: 'h1', s: 'Charte du joueur' },
    { t: 'sub', s: '{club} · saison {saison}' },
    { t: 'p', s: 'Ce que {lic} s’engage à faire pour son équipe et pour le club, pendant la saison {saison}.' },
    { t: 'h2', s: 'Je joue pour l’équipe' },
    { t: 'p', s: 'Je viens aux entraînements, je préviens quand je ne peux pas venir, j’encourage mes partenaires.' },
    { t: 'h2', s: 'Je respecte le jeu' },
    { t: 'p', s: 'J’accepte les décisions des arbitres et des officiels de table, je salue l’adversaire avant et après le match.' },
    { t: 'h2', s: 'Je prends soin du club' },
    { t: 'p', s: 'Je range le matériel, je laisse le vestiaire propre, je donne un coup de main aux matchs à domicile quand je peux.' },
  ];
  return [{ t: 'h1', s: 'Titre du document' }, { t: 'sub', s: '{club} · saison {saison}' }, { t: 'p', s: '' }];
}

// ---------------------------------------------------------------------------------------------------------------------
// Ce qui change d'une version à l'autre : par article (un titre et ce qui le suit), puis mot à mot dans un paragraphe.
// ---------------------------------------------------------------------------------------------------------------------
interface Section { head: number | null; key: string; num: string | null; blocks: number[] }
const ART = /^\s*(article\s+(\d+)\s*[·.:–—-]?\s*)/i;
const blockText = (b: Block) => (b.t === 'ul' ? b.li.join('\n') : b.t === 'enc' ? `enc:${b.who}:${JSON.stringify(b.boxes)}` : b.s);

function sections(body: Body): Section[] {
  const out: Section[] = [{ head: null, key: '\u0000debut', num: null, blocks: [] }];
  body.forEach((b, i) => {
    if (b.t === 'h2') {
      const m = ART.exec(b.s);
      out.push({ head: i, key: plainText(b.s.replace(ART, '')).trim().toLowerCase(), num: m?.[2] ?? null, blocks: [] });
    } else out[out.length - 1]!.blocks.push(i);
  });
  return out;
}

/** La plus longue sous-suite commune : les paires d'indices (a, b) qui se correspondent. */
function lcs<T>(a: T[], b: T[], eq: (x: T, y: T) => boolean): [number, number][] {
  const n = a.length, m = b.length;
  const t: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) t[i]![j] = eq(a[i]!, b[j]!) ? t[i + 1]![j + 1]! + 1 : Math.max(t[i + 1]![j]!, t[i]![j + 1]!);
  const out: [number, number][] = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (eq(a[i]!, b[j]!)) { out.push([i, j]); i++; j++; } else if (t[i + 1]![j]! >= t[i]![j + 1]!) i++; else j++;
  }
  return out;
}

/** Le nouveau texte, les mots ajoutés entre ==…== (les mots retirés ne se montrent pas : on lit la nouvelle version). La
 *  ponctuation compte comme un mot : « dans l’app==, au plus tard le jeudi soir==. » */
export function markWords(before: string, after: string): string {
  const split = (s: string) => s.split(/(\s+|[,.;:!?…«»()])/).filter(Boolean);
  const isSpace = (t: string) => /^\s+$/.test(t);
  const wa = split(before).filter(t => !isSpace(t)), tb = split(after), wb = tb.filter(t => !isSpace(t));
  if (wa.length * wb.length > 400_000) return before === after ? after : `==${after}==`;
  const kept = new Set(lcs(wa, wb, (x, y) => x === y).map(([, j]) => j));
  let out = '', k = -1, open = false, space = '';
  for (const t of tb) {
    if (isSpace(t)) { space += t; continue; }
    const add = !kept.has(++k);
    if (add && !open) { out += `${space}==`; open = true; }
    else if (!add && open) { out += `==${space}`; open = false; }
    else out += space;
    space = '';
    out += t;
  }
  return `${out}${open ? '==' : ''}${space}`;
}

export interface Change { kind: 'modifié' | 'ajouté' | 'renuméroté' | 'retiré'; head: string; excerpt?: string; was?: string }
export interface Diff {
  changes: Change[];
  /** Pour chaque bloc de la nouvelle version : l'étiquette du titre (« modifié », « nouveau ») et le texte marqué. */
  marks: Map<number, { tag?: 'modifié' | 'nouveau'; s?: string; li?: string[] }>;
}

/** Ce qui change entre deux versions (sans version d'avant : rien ne change). */
export function diffBodies(before: Body | null, after: Body): Diff {
  const marks: Diff['marks'] = new Map();
  const changes: Change[] = [];
  if (!before) return { changes, marks };
  const SA = sections(before), SB = sections(after);
  const pairs = lcs(SA, SB, (x, y) => x.key === y.key);
  const matchedA = new Set(pairs.map(p => p[0])), matchedB = new Set(pairs.map(p => p[1]));
  const headOf = (body: Body, s: Section) => (s.head === null ? 'Le début du document' : plainText((body[s.head] as { s: string }).s));
  const whole = (y: number) => {
    const nb = after[y]!;
    if (nb.t === 'ul') marks.set(y, { li: nb.li.map(t => (t ? `==${t}==` : t)) });
    else if ('s' in nb && nb.s) marks.set(y, { s: `==${nb.s}==` });
  };
  for (const [ia, ib] of pairs) {
    const a = SA[ia]!, b = SB[ib]!;
    // Les blocs de l'article, appariés par leur sorte, dans l'ordre.
    const same = (x: number, y: number) => {
      const o = before[x]!, n = after[y]!;
      return o.t === n.t && (o.t !== 'enc' || o.who === (n as { who: Who }).who);
    };
    const inner = lcs(a.blocks, b.blocks, same);
    const pairedB = new Map(inner.map(([x, y]) => [b.blocks[y]!, a.blocks[x]!]));
    let excerpt: string | undefined;
    let changed = inner.length < a.blocks.length;
    for (const y of b.blocks) {
      const nb = after[y]!, x = pairedB.get(y);
      if (x === undefined) { changed = true; whole(y); excerpt ??= firstWords(nb); continue; }
      const ob = before[x]!;
      if (blockText(ob) === blockText(nb)) continue;
      changed = true;
      if (nb.t === 'ul' && ob.t === 'ul') {
        const li = nb.li.map((t, k) => (ob.li.includes(t) ? t : markWords(ob.li[k] ?? '', t)));
        marks.set(y, { li });
        excerpt ??= around(li.find(t => t.includes('==')) ?? '');
      } else if (nb.t === 'enc') excerpt ??= 'L’encart de signature change.';
      else if ('s' in nb && 's' in ob) {
        const s = markWords(ob.s, nb.s);
        marks.set(y, { s });
        excerpt ??= around(s);
      }
    }
    const renum = a.head !== null && b.head !== null && a.num !== b.num;
    if (changed) {
      if (b.head !== null) marks.set(b.head, { tag: 'modifié' });
      changes.push({ kind: 'modifié', head: headOf(after, b), excerpt: excerpt || undefined });
    } else if (renum) changes.push({ kind: 'renuméroté', head: headOf(after, b), was: a.num ? `l’ancien article ${a.num}` : undefined });
  }
  SB.forEach((s, ib) => {
    if (matchedB.has(ib)) return;
    if (s.head !== null) marks.set(s.head, { tag: 'nouveau' });
    s.blocks.forEach(whole);
    changes.push({ kind: 'ajouté', head: headOf(after, s), excerpt: s.blocks.map(y => firstWords(after[y]!)).find(Boolean) });
  });
  SA.forEach((s, ia) => { if (!matchedA.has(ia)) changes.push({ kind: 'retiré', head: headOf(before, s) }); });
  // Dans l'ordre de la nouvelle version, les retraits à la fin.
  const pos = (c: Change) => (c.kind === 'retiré' ? 1e9 : SB.findIndex(s => headOf(after, s) === c.head));
  changes.sort((x, y) => pos(x) - pos(y));
  return { changes, marks };
}

/** Les premiers mots d'un bloc (une phrase au plus). */
function firstWords(b: Block): string {
  const t = plainText(b.t === 'ul' ? b.li[0] ?? '' : 's' in b ? b.s : '').trim();
  const end = t.search(/[.!?](\s|$)/);
  const one = end > 0 ? t.slice(0, end + 1) : t;
  return one.length > 90 ? `${one.slice(0, 88).replace(/\s+\S*$/, '')}…` : one;
}
/** Le passage autour du premier changement : six mots avant, jusqu'à la fin de la phrase. */
function around(marked: string): string {
  const i = marked.indexOf('==');
  if (i < 0) return '';
  const j = marked.indexOf('==', i + 2);
  const endMark = j < 0 ? marked.length : j + 2;
  const stop = marked.slice(endMark).search(/[.!?](\s|$)/);
  const end = stop < 0 ? marked.length : endMark + stop + 1;
  const words = marked.slice(0, i).split(' ');
  const lead = words.length > 6 ? `… ${words.slice(-6).join(' ')}` : marked.slice(0, i);
  let tail = marked.slice(i, end);
  if ((tail.match(/==/g) ?? []).length % 2) tail += '==';
  return plainText(`${lead}${tail}`.replace(/\*\*/g, '')).length ? `${lead}${tail}`.replace(/\*\*/g, '') : '';
}

// ---------------------------------------------------------------------------------------------------------------------
// L'export : la liste des signatures en tableur (une ligne par fiche)
// ---------------------------------------------------------------------------------------------------------------------
export interface ExportSig {
  doc: string; role: Who; title: string; version: number; sha256: string; season: string | null; first: string; last: string | null;
  minor: boolean; source: 'app' | 'papier'; signer: string; quality: string; email: string | null; text: string;
  boxes: { t: string; req: boolean; ok: boolean }[] | null; paper_on: string | null; signed_at: string;
}
const cell = (v: string | number | null | undefined) => {
  const s = v == null ? '' : String(v);
  return /[;"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const stamp = (iso: string, tz?: string) => new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', ...(tz ? { timeZone: tz } : {}) });

/** Le tableur (CSV au point-virgule, lu tel quel par un tableur français), avec son BOM. */
export function signaturesCsv(rows: ExportSig[], tz?: string): string {
  const head = ['Document', 'Version', 'Empreinte SHA-256 de la version', 'Saison', 'Licencié', 'Mineur', 'Encart', 'Signé par', 'Qualité',
    'Comment', 'Signé le', 'Sur papier le', 'Case cochée', 'Cases d’autorisation', 'Compte'];
  const lines = rows.map(r => [r.title, r.version, r.sha256, r.season, `${r.first}${r.last ? ` ${r.last}` : ''}`, r.minor ? 'oui' : 'non',
    r.role === 'licencie' ? 'Le licencié' : 'Signature', r.signer, r.quality, r.source === 'papier' ? 'sur papier' : 'en ligne',
    stamp(r.signed_at, tz), r.paper_on ? dayFr(r.paper_on) : '', r.text,
    (r.boxes ?? []).map(b => `${b.ok ? '[x]' : '[ ]'} ${b.t}${b.req ? ' (obligatoire)' : ''}`).join(' | '), r.email ?? ''].map(cell).join(';'));
  return `﻿${[head.map(cell).join(';'), ...lines].join('\r\n')}\r\n`;
}

/** Un nom de fichier sûr : « Règlement intérieur - version 2.pdf ». */
export const fileSafe = (s: string) => s.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim().slice(0, 80);
